import { useEffect, useState, type FC } from 'react';
import useSWR from 'swr';
import PricingInputField from '../PricingInputField/PricingInputField';
import { SWR_KEYS } from '../../../../../../../constants/constants';
import { countriesList, getCountriesOpenForSellingWhitProviders } from '../../../../../../../services/countriesList';
import { NudosDropdownWithSearchbar, NudosMaskedNumberInput } from '../../../../../../../components/NudosComponents';
import { ICountryDetail, ICountryExchangeDetail, IProviderDetails } from '../../../../../../../types/countries';
import { CloseNewCountryIcon } from '../../../../../../../assets/images/countryList';
import { Form } from 'antd';
import { roundUpNumber } from '../../../../../../../utils/formatNumbers';
import { IProductDetailId } from '../../../../../../../types/products';
import './PricingForm.scss';

interface PricingFormProps {
  setShowPricingForm?: (data: boolean) => void;
  setVerifyPricing: (areVerified: boolean) => void;
  availableProviders?: IProviderDetails[];
  productDataDetail?: IProductDetailId;
  showPricingForm: boolean;
}

const PricingForm: FC<PricingFormProps> = ({
  setShowPricingForm,
  setVerifyPricing,
  showPricingForm = false,
  productDataDetail
}) => {
  const { data: countriesListData } = useSWR([SWR_KEYS.countriesList], () => countriesList('buy&order=name'));
  const { data: countriesOpenForSellingWhitProvidersData } = useSWR(
    [SWR_KEYS.countriesOpenForSale],
    getCountriesOpenForSellingWhitProviders
  );

  const [currentCountry, setCurrentCountry] = useState<ICountryDetail[]>();

  const { setFieldsValue } = Form.useFormInstance();
  const providerPrice = Form.useWatch(`providerPrice`);
  const margin = Form.useWatch(`margin`);
  const exchangeRate = Form.useWatch(`exchangeRate`);
  const taxRate = Form.useWatch(`taxRate`);
  const nudosPriceUsd = Form.useWatch(`nudosPriceUsd`);
  const nudosPriceUsdWithTaxes = Form.useWatch(`nudosPriceUsdWithTaxes`);
  const countryData = Form.useWatch(`countryData`);
  const [newProvider, setNewProvider] = useState<IProviderDetails>();
  const isMarginValid = !margin || Math.abs(margin) < 100;
  const isProviderPriceValid = !providerPrice || +providerPrice > 0;
  const isNudosPriceUsdWithTaxesInputValid = !nudosPriceUsdWithTaxes || +nudosPriceUsdWithTaxes > 0;
  const negativeOrZeroMarginAlert = !isMarginValid || (margin <= 0 && nudosPriceUsdWithTaxes) ? ' ' : undefined;
  const emptyFieldPlaceholder = '$0 USD';
  const renderCountryRequired = !productDataDetail?.productId
    ? true
    : !productDataDetail?.pricingData.length || showPricingForm;

  const pricingFields = [
    {
      componentSize: 'extraSmall',
      dataName: `taxRate`,
      label: 'Impuestos',
      initialValue: undefined,
      currentValue: taxRate,
      suffixSymbol: '%',
      placeholder: '%',
      isRequired: false,
      isDisabled: true
    },
    {
      componentSize: 'extraSmall',
      dataName: `exchangeRate`,
      label: 'Tasa de cambio',
      initialValue: undefined,
      currentValue: exchangeRate,
      suffixSymbol: countryData?.currencyCode ?? '',
      placeholder: `USD`,
      isRequired: false,
      isDisabled: true
    }
  ];
  const productPricingDataByCountry = productDataDetail?.pricingData;

  const filterRepeatedCountries = countriesListData?.filter(country => {
    return !productPricingDataByCountry?.some(product => product.nameCode === country.code);
  });

  const formDropdownOptionsFnProviders = (
    setCurrentOption: React.Dispatch<React.SetStateAction<IProviderDetails | undefined>>
  ) => {
    return (rawOptions: IProviderDetails[], clickOptionCallback: (option: IProviderDetails) => void) => {
      return rawOptions.map((option, i) => {
        const handleSelectThisOption = () => {
          setCurrentOption(option);
          setFieldsValue({
            ['providerId']: option?.id ?? ''
          });
          setFieldsValue({
            ['availableProviders']: rawOptions ?? ''
          });
          clickOptionCallback && clickOptionCallback(option);
        };
        return (
          <div className="optionContainer" key={`option-${option.id}-${i}`} onClick={handleSelectThisOption}>
            {option.name}
          </div>
        );
      });
    };
  };

  const formDropdownOptionsFnContries = (
    setCurrentOption: React.Dispatch<React.SetStateAction<ICountryDetail[] | undefined>>
  ) => {
    return (rawOptions: ICountryDetail[], clickOptionCallback: (option: ICountryDetail) => void) => {
      return rawOptions.map((option, i) => {
        const handleSelectThisOption = () => {
          setCurrentOption(option as ICountryDetail[]);
          setFieldsValue({
            ['newCountry']: option?.id ?? ''
          });

          setFieldsValue({
            ['countryData']: option ?? ''
          });
          setFieldsValue({
            ['exchangeRate']: option?.exchangeRate ? +option?.exchangeRate : 0
          });
          setFieldsValue({
            ['taxRate']: option?.taxRate ? +option?.taxRate : 0
          });

          clickOptionCallback && clickOptionCallback(option);
        };
        return (
          <div className="optionContainer" key={`option-${option.id}-${i}`} onClick={handleSelectThisOption}>
            {option.name}
          </div>
        );
      });
    };
  };

  const filterBySearchStringCallbackCountries = (search: string, rawOptions: ICountryDetail[]) => {
    return rawOptions.filter(taxData => taxData?.name?.toLowerCase().trim().includes(search.toLowerCase().trim()));
  };

  const filterBySearchStringCallbackProviders = (search: string, rawOptions: IProviderDetails[]) => {
    return rawOptions.filter(taxData => taxData?.name?.toLowerCase().trim().includes(search.toLowerCase().trim()));
  };

  const providersList = (code: string, countryProviders: ICountryExchangeDetail[]) => {
    if (code && countryProviders) {
      const myCountriesList = countryProviders.find(element => element.code === code);
      if (myCountriesList?.providers) {
        return myCountriesList.providers;
      }
      return [];
    }
    return [];
  };

  const getEcommercePriceSelectLabel = (includesTax: boolean) => {
    if (includesTax) {
      return <label className="ecommercePriceLabel labelSize">Precio tienda con impuestos</label>;
    }
    return <label className="ecommercePriceLabel labelSize">Precio sin impues.</label>;
  };

  const calculateMarginProportion = (marginParam?: number) => {
    if (typeof marginParam !== 'number' || Math.abs(marginParam) >= 100) return;
    const getMarginProportion = marginParam / 100;
    const calculatedMarginFactor = 1 - getMarginProportion;
    return calculatedMarginFactor;
  };

  const calculateSellingPriceField = (margin: number, includesTax: boolean) => {
    const calculatedMargin = calculateMarginProportion(margin);
    const buyingTaxFactor = +taxRate / 100 + 1;
    const subtotal = calculatedMargin ? +providerPrice / calculatedMargin : 0;
    if (isNaN(subtotal)) return 0;
    const usdTotalPreTax = +(subtotal / exchangeRate);
    const usdTotalPlusTax = +(subtotal / exchangeRate) * buyingTaxFactor;
    if (!includesTax) return usdTotalPreTax;
    if (includesTax) return usdTotalPlusTax;
  };

  const calculateMarginField = (nudosPriceUsdWithTaxes: number) => {
    // This formula is obtained solving for the margin in the one used to obtain the selling price
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const providerPriceTimesTaxes = +providerPrice ? providerPrice * taxFactor : 0;
    const exchangeTimesTotal = +exchangeRate && +nudosPriceUsdWithTaxes ? exchangeRate * nudosPriceUsdWithTaxes : 0;
    const providerPriceToSellingPriceProportion = exchangeTimesTotal ? providerPriceTimesTaxes / exchangeTimesTotal : 1;
    const calculatedMargin = 1 - providerPriceToSellingPriceProportion;
    return calculatedMargin * 100;
  };

  const handleChangeMargin = (newMarginInput: string) => {
    setFieldsValue({
      [`margin`]: newMarginInput
    });
    if (newMarginInput?.length < 1) {
      // To diferenciate the behaviour between a "0" margin and the empty string
      setFieldsValue({
        [`nudosPrice`]: ''
      });
      setFieldsValue({
        [`nudosPriceUsd`]: ''
      });
      setFieldsValue({
        [`nudosPriceWithTaxes`]: ''
      });
      setFieldsValue({
        [`nudosPriceUsdWithTaxes`]: ''
      });
      return;
    }
    const newMargin = isNaN(+newMarginInput) ? 0 : +newMarginInput;
    const newSellingPricePreTax = calculateSellingPriceField(newMargin, false);
    const newSellingPriceAfterTax = calculateSellingPriceField(newMargin, true);
    setFieldsValue({
      [`nudosPriceUsd`]: newSellingPricePreTax ? `${roundUpNumber(newSellingPricePreTax, 2)}` : ''
    });
    setFieldsValue({
      [`nudosPrice`]: newSellingPricePreTax ? `${roundUpNumber(newSellingPricePreTax * +exchangeRate, 2)}` : ''
    });
    setFieldsValue({
      [`nudosPriceUsdWithTaxes`]: newSellingPriceAfterTax ? `${roundUpNumber(newSellingPriceAfterTax, 2)}` : ''
    });
    setFieldsValue({
      [`nudosPriceWithTaxes`]: newSellingPriceAfterTax
        ? `${roundUpNumber(newSellingPriceAfterTax * +exchangeRate, 2)}`
        : ''
    });
  };

  const handleChangeNudosPriceUsdWithTaxes = (nudosPriceUsdWithTaxesInput: string) => {
    setFieldsValue({
      [`nudosPriceUsdWithTaxes`]: nudosPriceUsdWithTaxesInput
    });
    if (nudosPriceUsdWithTaxesInput?.length < 1) {
      // To diferenciate the behaviour between a "0" nudosPriceUsdWithTaxesInput and the empty string
      setFieldsValue({
        [`nudosPriceWithTaxes`]: ''
      });
      setFieldsValue({
        [`nudosPriceUsd`]: ''
      });
      setFieldsValue({
        [`nudosPrice`]: ''
      });
      setFieldsValue({
        [`margin`]: ''
      });
      return;
    }
    const sellingPriceNumber = nudosPriceUsdWithTaxesInput ? +nudosPriceUsdWithTaxesInput : 0;
    const calculatedMargin = calculateMarginField(sellingPriceNumber);
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const newPricePreTax = sellingPriceNumber ? sellingPriceNumber / taxFactor : 0;
    setFieldsValue({
      [`nudosPriceWithTaxes`]: sellingPriceNumber ? `${roundUpNumber(sellingPriceNumber * +exchangeRate, 2)}` : ''
    });
    setFieldsValue({
      [`nudosPriceUsd`]: newPricePreTax ? `${roundUpNumber(newPricePreTax, 2)}` : ''
    });
    setFieldsValue({
      [`nudosPrice`]: newPricePreTax && exchangeRate ? `${roundUpNumber(newPricePreTax * +exchangeRate, 2)}` : ''
    });
    setFieldsValue({
      [`margin`]: `${roundUpNumber(calculatedMargin, 4)}`
    });
  };

  const handleChangeProviderPrice = (providerPriceInput: string) => {
    setFieldsValue({
      [`providerPrice`]: providerPriceInput
    });
    handleChangeMargin(margin);
  };

  useEffect(() => {
    const pricingValuesAreValid = isMarginValid && isProviderPriceValid && isNudosPriceUsdWithTaxesInputValid;
    const hasRequiredFields = !!countryData && !!margin && !!providerPrice && !!nudosPriceUsdWithTaxes && pricingValuesAreValid
    const isVerified =
      hasRequiredFields && pricingValuesAreValid && typeof taxRate === 'number' && typeof exchangeRate === 'number';
    setVerifyPricing && setVerifyPricing(showPricingForm ? isVerified : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [margin, providerPrice, taxRate, exchangeRate, setVerifyPricing]);

  return (
    <div className="pricingForm">
      <div className="pricingFormContainer">
        <Form.Item name="newCountry" required={renderCountryRequired}>
          <NudosDropdownWithSearchbar
            label="País"
            componentSize="mediumSmall"
            customClassLabel="customClassLabel"
            formDropdownOptionsCallback={formDropdownOptionsFnContries(setCurrentCountry)}
            filterBySearchStringCallback={filterBySearchStringCallbackCountries}
            customPlaceholder="Selecciona el país"
            currentValue={currentCountry as ICountryDetail}
            isFilled={!!currentCountry}
            rawOptions={(filterRepeatedCountries as ICountryDetail[]) ?? []}
          />
        </Form.Item>
        <NudosMaskedNumberInput
          componentSize="small"
          label={`Costo de compra`}
          placeholder={`$0`}
          currentValue={providerPrice}
          handleChange={inputText => handleChangeProviderPrice(inputText || '')}
          maskPrefix={'$'}
          maskSufix={`${countryData?.currencyCode}`}
          roundToClosest
          numberOfDecimals={2}
          errorText={!isProviderPriceValid ? ' ' : undefined}
        />
        <NudosMaskedNumberInput
          label={`Margen`}
          componentSize="extraSmall"
          placeholder={'0 %'}
          currentValue={`${margin || ''}`}
          handleChange={inputText => handleChangeMargin(inputText || '')}
          maskPrefix={''}
          maskSufix={'%'}
          roundToClosest
          numberOfDecimals={4}
          errorText={negativeOrZeroMarginAlert}
          inputPadding={12}
        />
        {pricingFields.map(field => {
          return (
            <PricingInputField
              key={field.dataName}
              componentSize={field.componentSize}
              dataName={field.dataName}
              label={field.label}
              initialValue={typeof field.initialValue === 'number' ? field.initialValue : 0}
              currentValue={typeof field.currentValue === 'number' ? field.currentValue : 0}
              suffixSymbol={field.suffixSymbol}
              placeholder={field.placeholder}
              isRequired={field.isRequired}
              isDisabled={field.isDisabled}
            />
          );
        })}
        <NudosMaskedNumberInput
          componentSize="small"
          label={getEcommercePriceSelectLabel(false)}
          placeholder={emptyFieldPlaceholder}
          currentValue={nudosPriceUsd}
          maskPrefix={'$'}
          maskSufix={'USD'}
          roundToClosest
          numberOfDecimals={2}
          isDeactivated={true}
        />
        <NudosMaskedNumberInput
          componentSize="small"
          label={getEcommercePriceSelectLabel(true)}
          placeholder={emptyFieldPlaceholder}
          isDeactivated={false}
          handleChange={inputText => handleChangeNudosPriceUsdWithTaxes(inputText || '')}
          currentValue={nudosPriceUsdWithTaxes}
          maskPrefix={'$'}
          maskSufix={' USD'}
          roundToClosest
          numberOfDecimals={2}
          errorText={!isNudosPriceUsdWithTaxesInputValid ? ' ' : undefined}
        />

        <NudosDropdownWithSearchbar
          label="Proveedor"
          componentSize="mediumSmall"
          customClassLabel="customClassLabel"
          formDropdownOptionsCallback={formDropdownOptionsFnProviders(setNewProvider)}
          filterBySearchStringCallback={filterBySearchStringCallbackProviders}
          customPlaceholder="Proveedor"
          currentValue={newProvider}
          isFilled={!!newProvider}
          rawOptions={
            providersList(countryData?.code, countriesOpenForSellingWhitProvidersData as ICountryExchangeDetail[]) || []
          }
        />
        <Form.Item name="countryData" hidden required={renderCountryRequired} />
        <Form.Item name={`isActive`} hidden />
        <Form.Item name={`name`} hidden />
        <Form.Item name={`flag`} hidden />
        <Form.Item name={`inStock`} hidden />
        <Form.Item name={`providerPrice`} hidden />
        <Form.Item name={`margin`} hidden />
        <Form.Item name={`nudosPriceUsd`} hidden />
        <Form.Item name={`nudosPrice`} hidden />
        <Form.Item name={`nudosPriceUsdWithTaxes`} hidden />
        <Form.Item name={`nudosPriceWithTaxes`} hidden />
        <Form.Item name={`providerId`} hidden />
        <Form.Item name={`taxRate`} hidden />
        <Form.Item name={`availableProviders`} hidden />
      </div>
      <div className="closeFormContainer">
        <div className="cursor-pointer">
          <CloseNewCountryIcon
            onClick={() => {
              if (setShowPricingForm) {
                setShowPricingForm(false);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PricingForm;
